import { useState, createContext, useEffect } from 'react';
import 'primereact/resources/themes/saga-green/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import apiKeycloak from '../api/apiKeycloak';
import queryString from 'querystring';
import jwt from 'jwt-decode';

export const AuthContext = createContext({});

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [loading, setLoading] = useState(true);

  // Restaura e valida a sessão do utilizador ao carregar a aplicação
  useEffect(() => {
    async function loadStorageData() {
      const storageUser = localStorage.getItem('@App:user');
      const storageToken = localStorage.getItem('@App:token');

      if (storageUser && storageToken) {
        try {
          const parsedUser = JSON.parse(storageUser);
          await verificaToken(parsedUser);
        } catch (err) {
          limparSessaoLocal();
        }
      }
      setLoading(false);
    }

    loadStorageData();
  }, []);

  // Helper centralizado para limpar a sessão local
  function limparSessaoLocal() {
    localStorage.removeItem('@App:user');
    localStorage.removeItem('@App:token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }

  // Fazendo login do usuario
  async function signIn(email, password) {
    setLoadingAuth(true);

    try {
      const data = await auth_keycloak(email, password);

      if (data) {
        setUser(data);
        localStorage.setItem('@App:user', JSON.stringify(data));
        localStorage.setItem('@App:token', data.tokenInfo.access_token);
      }
      setLoadingAuth(false);
      return data;
    } catch (error) {
      setLoadingAuth(false);
      limparSessaoLocal();
      return null;
    }
  }

  async function auth_keycloak(email, password) {
    const form = {
      client_id: process.env.REACT_APP_API_KEYCLOAK_CLIENT_ID,
      username: email,
      password: password,
      grant_type: 'password',
      client_secret: process.env.REACT_APP_API_KEYCLOAK_CLIENT_SECRET,
      scope: 'openid',
    };

    // Obtendo o access token
    const resToken = await apiKeycloak.post(
      process.env.REACT_APP_API_KEYCLOAK_TOKEN_CLIENT,
      queryString.stringify(form),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }
    );

    const tokenTemp = resToken.data;

    // Obtendo as informações do usuário
    const resUserInfo = await apiKeycloak.post(
      process.env.REACT_APP_API_KEYCLOAK_USERINFO,
      {},
      {
        headers: { Authorization: `Bearer ${tokenTemp.access_token}` },
      }
    );

    return {
      uid: resUserInfo.data.sub,
      nome: resUserInfo.data.name,
      avatarUrl: resUserInfo.data.avatarUrl,
      email: resUserInfo.data.email,
      tokenInfo: tokenTemp,
      perfilUser: jwt(tokenTemp.access_token).realm_access?.roles || [],
    };
  }

  // Cadastrando um novo usuario
  async function signUp(email, password, nome) {
    setLoadingAuth(true);

    try {
      const resultadoNome = nome.trim().split(' ');
      const firstName = resultadoNome[0];
      const lastName = nome.trim().replace(firstName, '').trim();

      const formMaster = {
        client_id: 'admin-cli',
        grant_type: 'client_credentials',
        client_secret: process.env.REACT_APP_API_KEYCLOAK_ADMIN_SECRET,
        scope: 'openid',
      };

      const resMaster = await apiKeycloak.post(
        process.env.REACT_APP_API_KEYCLOAK_TOKEN_MASTER,
        queryString.stringify(formMaster),
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }
      );

      const tokenAdmin = resMaster.data.access_token;

      const dadosUsuario = {
        username: email,
        firstName: firstName,
        lastName: lastName,
        email: email,
        enabled: true,
        credentials: [
          {
            type: 'password',
            value: password,
            temporary: false,
          },
        ],
      };

      const response = await apiKeycloak.post(
        process.env.REACT_APP_API_KEYCLOAK_USERS,
        dadosUsuario,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokenAdmin}`,
          },
        }
      );

      setLoadingAuth(false);
      return response;
    } catch (error) {
      setLoadingAuth(false);
      return error.response;
    }
  }

  // Logout do usuario
  async function signOut() {
    if (user?.tokenInfo?.refresh_token) {
      const form = {
        client_id: process.env.REACT_APP_API_KEYCLOAK_CLIENT_ID,
        client_secret: process.env.REACT_APP_API_KEYCLOAK_CLIENT_SECRET,
        refresh_token: user.tokenInfo.refresh_token,
      };

      try {
        await apiKeycloak.post(
          process.env.REACT_APP_API_KEYCLOAK_LOGOUT,
          queryString.stringify(form),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              Authorization: `Bearer ${user.tokenInfo.access_token}`,
            },
          }
        );
      } catch (error) {
        console.warn('Erro na chamada de logout do Keycloak:', error);
      }
    }

    limparSessaoLocal();
  }

  // Verifica e renova o token caso esteja prestes a expirar
  async function verificaToken(userStorage) {
    const usuarioAtual = userStorage || user;

    if (!usuarioAtual?.tokenInfo?.access_token) {
      limparSessaoLocal();
      return;
    }

    const agora = Math.floor(Date.now() / 1000);
    const exp = jwt(usuarioAtual.tokenInfo.access_token).exp;
    const tempUser = { ...usuarioAtual };

    // Se faltar menos de 10 segundos para expirar, realiza o refresh
    if (agora > exp - 10) {
      const form = {
        client_id: process.env.REACT_APP_API_KEYCLOAK_CLIENT_ID,
        grant_type: 'refresh_token',
        client_secret: process.env.REACT_APP_API_KEYCLOAK_CLIENT_SECRET,
        refresh_token: tempUser.tokenInfo.refresh_token,
      };

      try {
        const res = await apiKeycloak.post(
          process.env.REACT_APP_API_KEYCLOAK_TOKEN_CLIENT,
          queryString.stringify(form),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }
        );

        tempUser.tokenInfo = res.data;

        // Atualiza a memória e a persistência do navegador
        setUser(tempUser);
        localStorage.setItem('@App:user', JSON.stringify(tempUser));
        localStorage.setItem('@App:token', tempUser.tokenInfo.access_token);
      } catch (error) {
        console.error('Refresh token expirado ou inválido (400 Bad Request):', error?.response?.data || error);
        
        // Limpa a sessão e força login
        limparSessaoLocal();
        window.location.href = '/login';
      }
    } else {
      setUser(tempUser);
    }
  }

  // Reseta senha via email
  async function resetPassword(email) {
    setLoadingAuth(true);

    try {
      const formMaster = {
        client_id: 'admin-cli',
        grant_type: 'client_credentials',
        client_secret: process.env.REACT_APP_API_KEYCLOAK_ADMIN_SECRET,
        scope: 'openid',
      };

      const resMaster = await apiKeycloak.post(
        process.env.REACT_APP_API_KEYCLOAK_TOKEN_MASTER,
        queryString.stringify(formMaster),
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }
      );

      const tokenAdmin = resMaster.data.access_token;

      const resUser = await apiKeycloak.get(
        `${process.env.REACT_APP_API_KEYCLOAK_USERS}?email=${email}`,
        {
          headers: { Authorization: `Bearer ${tokenAdmin}` },
        }
      );

      if (resUser.data?.length === 1) {
        const userId = resUser.data[0].id;
        const response = await apiKeycloak.put(
          `${process.env.REACT_APP_API_KEYCLOAK_USERS}${userId}/execute-actions-email`,
          JSON.stringify(['UPDATE_PASSWORD']),
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tokenAdmin}`,
            },
          }
        );
        setLoadingAuth(false);
        return response;
      } else {
        setLoadingAuth(false);
        return { status: 409 };
      }
    } catch (error) {
      setLoadingAuth(false);
      return error.response || { status: 500 };
    }
  }

  return (
    <AuthContext.Provider
      value={{
        signed: !!user,
        user,
        loading,
        signUp,
        signOut,
        signIn,
        loadingAuth,
        setUser,
        verificaToken,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;